import * as tokens from "@duetds/tokens"

import { DuetTheme } from "../common-types"
import turva2ColorTokens from "./turva2-color-tokens"

const allowedThemes: string[] = ["turva"]

const MATCH_CAPITAL_CASE = /[A-Z]/g

function isTurva2() {
  if (typeof document === "undefined") {
    return false
  }
  return !!document.documentElement.attributes.getNamedItem("class")?.value.includes("duet-theme-turva2")
}

export function isCamelCase(str) {
  const camel = MATCH_CAPITAL_CASE.test(str)
  const barredString = !str.includes("_") && !str.includes("-") && !str.includes(" ")

  return camel && barredString
}

export function isDashCase(str) {
  const dash = str.includes("-")
  const barredString = str.includes(" ") || str.includes("_")
  const barredStartingString = str.charAt(0) === "-"

  return dash && !barredString && !barredStartingString
}

export function capitalize(str: string = ""): string {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`
}

function lowerCaseFirst(str: string = "") {
  return `${str.charAt(0).toLowerCase()}${str.slice(1)}`
}

export function camelize(str: string = "") {
  const arr = str.split("-")

  //loop through each element of the array and capitalize the first letter.'
  arr.forEach((string, index) => {
    arr[index] = capitalize(string)
  })
  return arr.join("")
}

export function getTheme(theme: DuetTheme = ""): DuetTheme {
  if (allowedThemes.includes(theme.toLowerCase())) {
    return theme.toLowerCase() as DuetTheme
  }
  return ""
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

function hasType(t: string, str: string = ""): boolean {
  return str.indexOf(capitalizeFirstLetter(t)) === 0 || str.indexOf(t) === 0
}

function addPrefix(prefix: string, str: string = ""): string {
  if (str && !isDashCase(str) && !hasType(prefix, str)) {
    return `${prefix}${capitalize(str)}`
  }
  return str
}

function addTheme(str: string, theme: DuetTheme): string {
  const hasTheme = getTheme(theme).length
  if (hasTheme && str.indexOf(theme) === -1) {
    return `${str}${capitalize(theme)}`
  }
  return str
}

/**
 * Add "-turva" to color names (or "Turva" to camelCase color names)
 */
export function postfixTheme(name: string, theme: DuetTheme) {
  if (!name) {
    return undefined
  }
  if (["", "default"].includes(theme)) {
    return name
  }
  if ((isDashCase(name) && name.endsWith(`-${theme}`)) || (isCamelCase(name) && name.endsWith(capitalize(theme)))) {
    return name
  }
  if (tokens[`color${camelize(name)}${capitalizeFirstLetter(theme)}`]) {
    return !isCamelCase(name) ? `${name}-${theme}` : `${name}${capitalize(theme)}`
  }
  return name
}

export function getColorByName(name: string, theme: DuetTheme = ""): string | undefined {
  if (!name) {
    return undefined
  }

  if ((theme === "turva" || name.endsWith("-turva")) && isTurva2()) {
    switch (name.replace(/-turva$/, "")) {
      case "primary-lightest":
        return turva2ColorTokens["color-turva-nude-00"]
      case "primary-lighter":
        return turva2ColorTokens["color-turva-nude-50"]
      case "primary-light":
        return turva2ColorTokens["color-turva-nude-200"]
      case "primary":
        return turva2ColorTokens["color-turva2-red-600"]
      case "primary-darker":
        return turva2ColorTokens["color-turva2-red-700"]
      case "primary-dark":
        return turva2ColorTokens["color-turva2-red-800"]
      case "secondary":
        return turva2ColorTokens["color-turva2-gray-900"]
      case "gray-lighter":
        return turva2ColorTokens["color-turva2-gray-00"]
      case "gray-light":
        return turva2ColorTokens["color-turva2-gray-100"]
      case "gray":
        return turva2ColorTokens["color-turva2-gray-200"]
      case "gray-dark":
        return turva2ColorTokens["color-turva2-gray-400"]
      case "gray-darker":
        return turva2ColorTokens["color-turva2-gray-500"]
      default:
      // carry on to old turva colors
    }
  }
  const defaultColor = convertToColorName(name)
  const themedColor = theme && convertToColorName(name, theme)

  // return themed color if it exists
  return tokens[themedColor] || tokens[defaultColor]
}

export function convertToColorName(name: string = "", theme: DuetTheme = "") {
  let colorResolution = name

  const dash = isDashCase(name)

  if (dash) {
    colorResolution = camelize(colorResolution)
  }
  colorResolution = addPrefix("color", colorResolution)

  colorResolution = addTheme(colorResolution, theme)

  // takes care of the case where colorPrimaryDark ends up with ColorPrimaryDark
  colorResolution = lowerCaseFirst(colorResolution)

  return colorResolution
}

function getTokenByName(prefix: string, name: string): string | undefined {
  if (!name) {
    return undefined
  }
  let tokenResolution = name
  const dash = isDashCase(name)
  if (dash) {
    tokenResolution = camelize(tokenResolution)
  }
  tokenResolution = addPrefix(prefix, tokenResolution)
  tokenResolution = lowerCaseFirst(tokenResolution)

  try {
    return tokens[tokenResolution]
  } catch (e) {
    return undefined
  }
}

export function getFontSizeByName(name: string): string | undefined {
  return getTokenByName("fontSize", name)
}

export function getFontWeightByName(name: string): string | undefined {
  return getTokenByName("fontWeight", name)
}

export function getShadowByName(name: string): string | undefined {
  return getTokenByName("shadow", name)
}

export function rgbaColorFromRgb(color: string, opacity: number) {
  if (!color || (opacity !== 0 && !opacity)) {
    return undefined
  }

  const f = color.split(",")
  const R = parseInt(f[0].slice(4)),
    G = parseInt(f[1]),
    B = parseInt(f[2])

  return `rgba(${R},${G},${B},${opacity})`
}

export function shadeRGBColor(color: string, percent: number) {
  const f = color.split(","),
    t = percent < 0 ? 0 : 255,
    p = percent < 0 ? percent * -1 : percent
  let R = parseInt(f[0].slice(4)),
    G = parseInt(f[1]),
    B = parseInt(f[2])

  R = Math.round((t - R) * p) + R
  G = Math.round((t - G) * p) + G
  B = Math.round((t - B) * p) + B

  R = R < 0 ? 0 : R
  R = R > 255 ? 255 : R
  G = G < 0 ? 0 : G
  G = G > 255 ? 255 : G
  B = B < 0 ? 0 : B
  B = B > 255 ? 255 : B

  return `rgb(${R},${G},${B})`
}

// remove wrapping speech marks.
// media query tokens are wrapped in speech marks,
// which need to be removed before use with matchMedia.
const unwrap = (str: string) => str.substring(1, str.length - 1)

export const breakpointToToken = {
  "xxx-small": unwrap(tokens.mediaQueryXxxSmall),
  "xx-small": unwrap(tokens.mediaQueryXxSmall),
  "x-small": unwrap(tokens.mediaQueryXSmall),
  small: unwrap(tokens.mediaQuerySmall),
  medium: unwrap(tokens.mediaQueryMedium),
  large: unwrap(tokens.mediaQueryLarge),
  "x-large": unwrap(tokens.mediaQueryXLarge),
  "xx-large": unwrap(tokens.mediaQueryXxLarge),
  "xxx-large": unwrap(tokens.mediaQueryXxxLarge),
}

export const breakpointTokensUpTo: string[] = Object.keys(breakpointToToken).filter(
  key => key.indexOf("x-small") !== -1
)
export const breakpointTokensFrom: string[] = Object.keys(breakpointToToken).filter(
  key => !breakpointTokensUpTo.includes(key)
)

export type DuetBreakpoints = keyof typeof breakpointToToken
